import Highcharts from 'highcharts';
import { TFunction } from 'i18next';

/**
 * Turns a data point into a Highcharts point. Plain numbers are used for
 * measured values; extrapolated points become hollow, dashed-outline markers
 * carrying a `custom.projected` flag that the tooltip formatter picks up.
 */
function buildSeriesPoint(
    item: ChartData,
    seriesColor: string,
): number | Highcharts.PointOptionsObject {
    const value = Number(item.value ?? 0);
    if (!item.projected) return value;

    return {
        y: value,
        color: '#fff',
        borderColor: seriesColor,
        borderWidth: 2,
        dashStyle: 'ShortDash',
        marker: {
            enabled: true,
            radius: 5,
            symbol: 'diamond',
            fillColor: '#fff',
            lineColor: seriesColor,
            lineWidth: 2,
        },
        custom: { projected: true },
    };
}

/**
 * Base chart config for advanced charts (height 350) with period-based xAxis.
 * Used by CashFlowChart, ForecastChart, RetentionChart.
 */
export function createAdvancedChartBase(
    chartType: string,
    categories: readonly string[],
    xAxisTitle: string,
): Pick<Highcharts.Options, 'chart' | 'title' | 'xAxis'> {
    return {
        chart: {
            type: chartType,
            height: 350,
        },
        title: {
            text: undefined,
        },
        xAxis: {
            categories: [...categories],
            title: {
                text: xAxisTitle,
            },
        },
    };
}

/**
 * Dual-axis grouped column chart config.
 * Used by CustomerSegmentChart, PriceSimulationChart.
 */
export function createDualAxisGroupedConfig(
    categories: readonly string[],
    xAxisTitle: string,
    primaryAxisTitle: string,
    secondaryAxisTitle: string,
): Pick<Highcharts.Options, 'chart' | 'title' | 'xAxis' | 'yAxis' | 'tooltip' | 'plotOptions'> {
    return {
        chart: {
            type: 'column',
            height: 350,
        },
        title: {
            text: undefined,
        },
        xAxis: {
            categories: [...categories],
            title: {
                text: xAxisTitle,
            },
        },
        yAxis: [
            {
                title: {
                    text: primaryAxisTitle,
                },
                min: 0,
            },
            {
                title: {
                    text: secondaryAxisTitle,
                },
                min: 0,
                opposite: true,
            },
        ],
        tooltip: {
            shared: true,
        },
        plotOptions: {
            column: {
                grouping: true,
                borderWidth: 0,
            },
        },
    };
}

/**
 * Compact chart base config (height 280) with date-based xAxis and styled labels.
 * Used by FinancialPositionChart, GoldValueMultiChart.
 */
export function createCompactChartBase(
    chartType: string,
    categories: readonly string[],
    yAxisTitle: string,
): Pick<Highcharts.Options, 'chart' | 'title' | 'xAxis' | 'yAxis'> {
    return {
        chart: {
            type: chartType,
            height: 280,
            spacing: [8, 8, 8, 8],
            style: { fontFamily: 'inherit' },
        },
        title: { text: undefined },
        xAxis: {
            categories: [...categories],
            labels: { style: { fontSize: '11px', color: '#888' } },
        },
        yAxis: {
            title: {
                text: yAxisTitle,
                style: { fontSize: '12px', color: '#888' },
            },
            labels: {
                style: { fontSize: '11px' },
                formatter: function () {
                    return this.value?.toLocaleString() ?? '';
                },
            },
        },
    };
}

/**
 * Column chart with rotated xAxis labels and dual yAxis (min 0, second opposite).
 * Used by RetentionChart, SeasonalityChart.
 */
export function createRotatedLabelDualAxisConfig(
    categories: readonly string[],
    xAxisTitle: string,
    primaryAxisTitle: string,
    secondaryAxisTitle: string,
): Pick<Highcharts.Options, 'chart' | 'title' | 'xAxis' | 'yAxis'> {
    return {
        chart: {
            type: 'column',
            height: 350,
        },
        title: {
            text: undefined,
        },
        xAxis: {
            categories: [...categories],
            title: {
                text: xAxisTitle,
            },
            labels: {
                rotation: -45,
                style: {
                    fontSize: '10px',
                },
            },
        },
        yAxis: [
            {
                title: {
                    text: primaryAxisTitle,
                },
                min: 0,
            },
            {
                title: {
                    text: secondaryAxisTitle,
                },
                min: 0,
                opposite: true,
            },
        ],
    };
}

/** Resolve the display color for a Highcharts tooltip point, avoiding nested ternaries. */
export function resolvePointColor(
    pointColor: string | Highcharts.GradientColorObject | Highcharts.PatternObject | undefined,
    seriesColor: string | Highcharts.GradientColorObject | Highcharts.PatternObject | undefined,
): string {
    if (typeof pointColor === 'string') return pointColor;
    if (typeof seriesColor === 'string') return seriesColor;
    return '#666';
}

export interface ChartData {
    date: string;
    value: number | null;
    /**
     * Marks an extrapolated point (currently the running month of a monthly
     * series, scaled to a full month). Rendered hollow and labelled in the
     * tooltip so a forecast is not mistaken for a measured value.
     */
    projected?: boolean;
}

export interface ChartConfigOptions {
    chartType: 'column' | 'line' | 'spline';
    height?: number;
    yAxisTitle: string;
    yAxisColor: string;
    seriesName: string;
    seriesColor: string;
    valueSuffix?: string;
    referenceValue?: number;
}

export const createBaseChartConfig = (
    data: readonly ChartData[],
    options: ChartConfigOptions,
    t: TFunction,
): Highcharts.Options => {
    const {
        chartType,
        height = 210,
        yAxisTitle,
        yAxisColor,
        seriesName,
        seriesColor,
        valueSuffix = '',
        referenceValue,
    } = options;

    // Extract nested ternaries into a clear computed variable for Sonar readability
    const plotOptions: Highcharts.PlotOptions | undefined = (() => {
        switch (chartType) {
            case 'line':
                return { line: { marker: { enabled: true, radius: 4 } } };
            case 'column':
                return {
                    column: {
                        opacity: 0.8,
                        pointPadding: 0.1,
                        groupPadding: 0.1,
                        borderWidth: 0,
                    },
                };
            default:
                return undefined;
        }
    })();

    return {
        chart: {
            type: chartType,
            height,
            spacing: [8, 8, 8, 8],
            marginLeft: 70, // more space for y axis labels
            marginRight: 8,
            marginBottom: 48,
            marginTop: 8,
            style: { fontFamily: 'inherit' },
        },
        title: {
            text: undefined,
        },
        xAxis: {
            categories: data.map(item => item.date),
            title: {
                text: t('statistics.charts.date', 'Date'),
                style: { fontSize: '12px', color: '#888' },
            },
            labels: {
                style: { fontSize: '11px', color: '#888' },
                y: 8,
            },
            tickLength: 2,
            lineWidth: 1,
            lineColor: '#e0e0e0',
        },
        yAxis: {
            title: {
                text: yAxisTitle,
                style: {
                    color: yAxisColor,
                    fontSize: '12px',
                },
            },
            labels: {
                style: {
                    color: yAxisColor,
                    fontSize: '11px',
                },
                formatter: function () {
                    return this.value?.toLocaleString() ?? '';
                },
            },
            gridLineColor: '#f0f0f0',
            plotLines: referenceValue
                ? [
                      {
                          color: '#ff4d4f',
                          width: 2,
                          value: referenceValue,
                          dashStyle: 'Dash',
                          label: {
                              text: t('statistics.charts.referenceLabel', 'Reference'),
                              align: 'right',
                              style: {
                                  color: '#ff4d4f',
                                  fontSize: '11px',
                              },
                          },
                      },
                  ]
                : undefined,
        },
        series: [
            {
                name: seriesName,
                type: chartType,
                data: data.map(item => buildSeriesPoint(item, seriesColor)),
                color: seriesColor,
                tooltip: {
                    valueSuffix,
                    pointFormatter: function () {
                        let color = '#666';
                        if (typeof this.color === 'string') {
                            color = this.color;
                        } else if (this.series && typeof this.series.color === 'string') {
                            color = this.series.color;
                        }
                        const isProjected = Boolean(
                            (this.options.custom as { projected?: boolean } | undefined)?.projected,
                        );
                        const projectedNote = isProjected
                            ? ` <i>(${t('statistics.charts.projected', 'projected')})</i>`
                            : '';
                        return `<span style="color:${color}">●</span> ${this.series.name}: <b>${this.y?.toLocaleString()}${valueSuffix}</b>${projectedNote}<br/>`;
                    },
                },
            },
        ],
        legend: {
            enabled: false,
        },
        credits: {
            enabled: false,
        },
        tooltip: {
            shared: true,
            useHTML: true,
            backgroundColor: '#fff',
            borderColor: '#ccc',
            borderRadius: 4,
            padding: 8,
            style: { fontSize: '12px' },
        },
        // Provide sensible defaults to reduce duplication across charts
        plotOptions,
    };
};
